import Link from "../../components/router-link";
import { ArrowUpRight, Check } from "lucide-react";
import { CTA, PageHero, SectionHeading } from "../../components/site-shell";
import { createMetadata } from "../../lib/seo";

export const metadata = createMetadata({
  title: "品牌增长与出海服务价格",
  description:
    "查看品牌增长诊断、SEO/GEO 优化、企业官网、海外独立站、产品出海及持续增长运营的服务套餐、参考价格、适用企业与交付内容。",
  path: "/pricing",
  keywords: [
    "品牌增长服务价格",
    "企业增长诊断价格",
    "SEO优化价格",
    "GEO优化价格",
    "AI搜索优化价格",
    "企业官网建设价格",
    "独立站建设费用",
    "外贸独立站建设价格",
    "Google SEO费用",
    "产品出海服务费用",
    "海外营销服务价格",
    "内容营销服务价格",
    "持续增长运营费用",
  ],
  image: "/assets/office.jpg",
});

const packages = [
  {
    code: "D01",
    title: "初步增长诊断",
    tagline: "确认现状、问题与合作方向",
    audience: "希望先判断品牌、网站、内容、搜索或获客问题优先级的企业",
    price: "¥0",
    unit: "次",
    duration: "沟通后确认",
    focus: "业务现状与合作匹配",
    outcome: "形成当前问题判断与下一步沟通建议",
    deliverables: [
      "业务与目标市场沟通",
      "品牌与产品表达快检",
      "网站、搜索与线索路径快检",
      "下一步建议",
    ],
  },
  {
    code: "D02",
    title: "深度增长诊断",
    tagline: "系统梳理增长问题与行动路径",
    audience:
      "已有产品、网站或市场基础，需要明确增长问题、优先级与实施方案的企业",
    price: "¥3,800",
    unit: "次",
    duration: "按诊断范围确定",
    focus: "品牌、官网、内容与获客诊断",
    outcome: "获得问题清单、增长建议与阶段行动方案",
    deliverables: [
      "品牌与市场增长诊断",
      "官网、内容与搜索现状分析",
      "关键问题与优先级清单",
      "阶段性增长行动建议",
    ],
  },
  {
    code: "D03",
    title: "品牌曝光保障包",
    tagline: "持续建设品牌可见性与内容基础",
    audience:
      "希望提升行业曝光、完善网站内容，并建立 SEO、GEO 与运营基础的企业",
    price: "¥5,688",
    unit: "3个月",
    duration: "3个月",
    focus: "品牌曝光、SEO / GEO 与网站运营",
    outcome: "获得连续的内容、搜索与页面优化支持",
    deliverables: [
      "品牌与产品内容梳理",
      "SEO / GEO 基础建设",
      "网站内容与页面优化",
      "阶段数据跟踪与复盘",
    ],
  },
  {
    code: "D04",
    title: "增长获客保障包",
    tagline: "构建稳定的品牌曝光与客户入口",
    audience: "需要持续推进品牌表达、网站优化、内容搜索与线上获客基础的企业",
    price: "¥11,688",
    unit: "6个月",
    duration: "6个月",
    focus: "SEO / GEO、网站运营与获客路径",
    outcome: "形成更完整的内容、搜索、线索与复盘节奏",
    deliverables: [
      "增长主题与内容规划",
      "SEO / GEO 持续优化",
      "网站页面与咨询入口优化",
      "月度数据分析与增长复盘",
    ],
  },
  {
    code: "D05",
    title: "持续增长运营",
    tagline: "长期运营品牌、内容、搜索与客户路径",
    audience:
      "已具备基础品牌阵地，需要持续建设内容资产、搜索曝光和客户渠道的企业",
    price: "¥6,800/月 起",
    unit: "月",
    duration: "按合作范围确定",
    focus: "持续内容、搜索、数据与页面优化",
    outcome: "以稳定运营节奏持续积累品牌可见性与线索机会",
    deliverables: [
      "月度增长计划与主题",
      "内容与搜索资产运营",
      "网站与转化路径持续优化",
      "月度数据报告与复盘",
    ],
  },
  {
    code: "D06",
    title: "定制增长项目",
    tagline: "面向复杂业务的品牌增长共建",
    audience: "有定制化品牌增长、官网重构、内容体系或企业内协同需求的企业",
    price: "¥59,800 起",
    unit: "项目起",
    duration: "按项目确定",
    focus: "定制化增长策略与专项交付",
    outcome: "围绕企业业务目标完成定制的品牌增长建设与运营方案",
    deliverables: [
      "品牌与增长策略共创",
      "官网、内容或搜索专项建设",
      "跨团队协作与实施支持",
      "阶段复盘与运营机制",
    ],
  },
  {
    code: "G01",
    title: "海外增长初诊",
    tagline: "判断产品出海基础与市场方向",
    audience:
      "计划开拓海外客户，需初步判断产品、市场、网站与获客方向的制造企业、工厂和外贸企业",
    price: "¥0",
    unit: "次",
    duration: "沟通后确认",
    focus: "产品出海可行性与合作匹配",
    outcome: "明确海外增长的当前基础与下一步沟通方向",
    deliverables: [
      "产品与目标市场初步沟通",
      "现有网站与获客渠道快检",
      "海外搜索需求初步判断",
      "下一步建议",
    ],
  },
  {
    code: "G02",
    title: "海外增长深度诊断",
    tagline: "明确海外市场、客户与数字渠道路径",
    audience:
      "希望系统评估海外市场、客户需求、竞品、搜索机会与独立站基础的企业",
    price: "¥3,800",
    unit: "次",
    duration: "按诊断范围确定",
    focus: "海外市场、独立站与询盘路径",
    outcome: "形成产品出海问题清单、市场判断与增长建议",
    deliverables: [
      "目标市场与客户需求诊断",
      "竞品与海外搜索机会分析",
      "独立站与询盘路径评估",
      "出海增长优先级建议",
    ],
  },
  {
    code: "G03",
    title: "产品出海启动版",
    tagline: "建立海外品牌阵地与获客起点",
    audience: "首次系统建设海外独立站、品牌表达与数字获客基础的企业",
    price: "¥49,800 起",
    unit: "项目起",
    duration: "按项目确定",
    focus: "海外品牌表达、独立站与搜索基础",
    outcome: "完成面向目标市场的海外数字阵地首轮建设",
    deliverables: [
      "海外市场与产品表达梳理",
      "海外独立站核心页面建设",
      "Google SEO / GEO 基础",
      "Form、Email、WhatsApp 询盘承接",
    ],
  },
  {
    code: "G04",
    title: "产品出海标准版",
    tagline: "系统建设海外内容、搜索与询盘能力",
    audience: "需要以独立站为核心，持续拓展海外搜索曝光和客户询盘的企业",
    price: "¥79,800 起",
    unit: "项目起",
    duration: "按项目确定",
    focus: "海外站点、内容、搜索与询盘增长",
    outcome: "建立更完整的海外品牌资产与数字获客路径",
    deliverables: [
      "多层级产品与行业页面",
      "海外内容与信任体系建设",
      "Google SEO / GEO 主题布局",
      "询盘来源记录与运营机制",
    ],
  },
  {
    code: "G05",
    title: "产品出海深度版",
    tagline: "面向重点市场建立规模化增长能力",
    audience:
      "多市场、多语言、复杂产品或需要持续扩大海外影响力与询盘来源的企业",
    price: "¥129,800 起",
    unit: "项目起",
    duration: "按项目确定",
    focus: "多市场、多语言与规模化内容搜索增长",
    outcome: "建立可扩展的海外品牌、内容、搜索与运营体系",
    deliverables: [
      "跨市场品牌与产品表达",
      "多语言独立站与内容体系",
      "规模化 SEO / GEO 运营",
      "数据分析、线索治理与阶段复盘",
    ],
  },
  {
    code: "G06",
    title: "海外持续增长运营",
    tagline: "长期积累海外搜索曝光与询盘机会",
    audience:
      "已完成海外站点基础建设，需要稳定运营内容、搜索、页面与线索系统的企业",
    price: "¥9,800/月 起",
    unit: "月",
    duration: "按合作范围确定",
    focus: "海外内容、搜索、询盘与持续优化",
    outcome: "持续提升海外品牌可见性、内容资产与询盘承接效率",
    deliverables: [
      "海外内容主题与发布运营",
      "Google SEO / GEO 持续优化",
      "产品页面与询盘路径迭代",
      "月度数据复盘与增长建议",
    ],
  },
];

const packageGroups = [
  {
    number: "01",
    title: "先完成增长诊断",
    description:
      "通过国内或海外增长诊断，看清业务基础、关键问题和下一阶段优先事项。",
    items: [packages[0], packages[1], packages[6], packages[7]],
  },
  {
    number: "02",
    title: "建设品牌与获客基础",
    description:
      "面向国内市场建立品牌曝光与持续获客能力，或以海外独立站开启产品出海增长。",
    items: [packages[2], packages[3], packages[8], packages[9]],
  },
  {
    number: "03",
    title: "持续运营与规模化增长",
    description:
      "为已有基础的企业提供长期运营支持，或面向复杂业务与重点市场开展定制化建设。",
    items: [packages[4], packages[5], packages[10], packages[11]],
  },
];

export default function PricingPage() {
  return (
    <>
      <PageHero
        eyebrow="Service plans & pricing"
        title="品牌增长与产品出海服务价格"
        description="覆盖企业品牌增长与产品出海增长两条业务线，以清晰的服务范围支持从诊断、建设到长期运营的不同阶段。"
        image="/assets/office.jpg"
      />
      <section className="section-shell section-block-sm pricing-intro">
        <SectionHeading
          eyebrow="服务选择"
          title="先识别关键问题，再选择与业务阶段匹配的服务。"
          description="以下为公开参考服务与建议价格。具体交付范围、项目周期和合作方式，将根据产品复杂度、现有基础、目标市场与增长目标在诊断后确认。"
        />
      </section>
      <section className="section-muted section-block-sm">
        <div className="section-shell">
          <div className="pricing-groups">
            {packageGroups.map((group) => (
              <section className="pricing-group" key={group.number}>
                <header className="pricing-group-heading">
                  <span>{group.number}</span>
                  <div>
                    <h2>{group.title}</h2>
                    <p>{group.description}</p>
                  </div>
                </header>
                <div className="pricing-grid">
                  {group.items.map((item) => (
                    <article className="pricing-card" key={item.code}>
                      <div className="pricing-card-heading">
                        <div>
                          <span className="pricing-code">{item.code}</span>
                          <h3>{item.title}</h3>
                        </div>
                        <p>{item.tagline}</p>
                      </div>
                      <div className="pricing-price">
                        <span>参考价格</span>
                        <strong>{item.price}</strong>
                      </div>
                      <dl className="pricing-meta">
                        <div>
                          <dt>计价单位</dt>
                          <dd>{item.unit}</dd>
                        </div>
                        <div>
                          <dt>建议周期</dt>
                          <dd>{item.duration}</dd>
                        </div>
                        <div>
                          <dt>服务重点</dt>
                          <dd>{item.focus}</dd>
                        </div>
                      </dl>
                      <div className="pricing-summary">
                        <div className="pricing-audience">
                          <span>适合企业</span>
                          <p>{item.audience}</p>
                        </div>
                        <div className="pricing-outcome">
                          <span>核心成果</span>
                          <p>{item.outcome}</p>
                        </div>
                      </div>
                      <ul className="pricing-deliverables">
                        {item.deliverables.map((deliverable) => (
                          <li key={deliverable}>
                            <Check size={15} aria-hidden="true" />
                            {deliverable}
                          </li>
                        ))}
                      </ul>
                      <Link className="text-link pricing-link" href="/contact">
                        预约咨询 <ArrowUpRight size={16} />
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
      <section className="section-shell section-block-sm">
        <div className="pricing-notes">
          <span className="eyebrow">服务说明</span>
          <div>
            <h2>以诊断结果确认最适合的合作方式</h2>
            <p>
              实际交付内容、项目周期与服务金额将根据业务复杂度、企业现有基础、目标市场与阶段目标确认。增长深度关注长期品牌资产、搜索曝光和客户渠道建设，不承诺固定排名、固定询盘量或销售结果。
            </p>
          </div>
        </div>
      </section>
      <CTA
        title="不确定该从哪个服务开始？"
        description="预约一次初步增长诊断，先明确品牌、网站、内容、搜索与获客路径中的关键问题。"
      />
    </>
  );
}
